import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import CreateGameView from '@/views/CreateGameView.vue'
import JoinGameView from '@/views/JoinGameView.vue'
import PlayGameView from '@/views/PlayGameView.vue'
import CompletedGameView from '@/views/CompletedGameView.vue'
import LoadGameView from '@/views/LoadGameView.vue'
import CopyGameView from '@/views/CopyGameView.vue'
import DownloadTranscriptsView from '@/views/DownloadTranscriptsView.vue'
import InternalRoleDashboardView from '@/views/InternalRoleDashboardView.vue'
import TeacherApplicationView from '@/views/TeacherApplicationView.vue'
import TeacherGroupsView from '@/views/TeacherGroupsView.vue'
import { useAuthStore } from '@/stores/authStore'
import { APP_ROUTE_NAMES, PERMISSIONS, USER_ROLES, getDefaultRoleRoute, getPostAuthLandingRoute, userHasAnyRole, userHasPermission } from '@/utils/permissions'
const routes = [
  {
    path: '/',
    name: 'Saboteur',
    component: HomeView,
    meta: {
      redirectAuthenticatedToDashboard: true,
    }
  },
  {
    path: '/home',
    name: APP_ROUTE_NAMES.HOME,
    component: HomeView,
    meta: {
      redirectAuthenticatedToDashboard: true,
    }
  },
  {
    path: '/create-game',
    name: 'create-game',
    component: CreateGameView
  },
  {
    path: '/join-game',
    name: 'join-game',
    component: JoinGameView
  },
  {
    path: '/play-game',
    name: 'play-game',
    component: PlayGameView
  },
  {
    path: '/thank-you',
    name: 'thank-you',
    component: CompletedGameView
  },
  {
    path: '/load-game',
    name: 'load-game',
    component: LoadGameView
  },
  {
    path: '/copy-game',
    name: 'copy-game',
    component: CopyGameView
  },
  {
    path: '/download-transcripts-sab514',
    name: 'download-transcripts',
    component: DownloadTranscriptsView
  },
  {
    path: '/admin',
    name: APP_ROUTE_NAMES.ADMIN,
    component: InternalRoleDashboardView,
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.ADMIN],
      dashboardRole: USER_ROLES.ADMIN,
    }
  },
  {
    path: '/teacher',
    name: APP_ROUTE_NAMES.TEACHER,
    component: InternalRoleDashboardView,
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.TEACHER],
      dashboardRole: USER_ROLES.TEACHER,
    }
  },
  {
    path: '/researcher',
    name: APP_ROUTE_NAMES.RESEARCHER,
    component: InternalRoleDashboardView,
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.RESEARCHER],
      dashboardRole: USER_ROLES.RESEARCHER,
    }
  },
  {
    // Opened in its own browser window from Game Tracking.
    path: '/replay/:gameId(\\d+)',
    name: APP_ROUTE_NAMES.GAME_REPLAY,
    component: () => import('@/views/GameReplayView.vue'),
    props: route => ({ gameId: Number(route.params.gameId) }),
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.TEACHER, USER_ROLES.RESEARCHER],
    }
  },
  {
    path: '/teacher-application',
    name: 'teacher-application',
    component: TeacherApplicationView
  },
  {
    path: '/teacher/groups',
    name: APP_ROUTE_NAMES.TEACHER_GROUPS,
    component: TeacherGroupsView,
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.TEACHER],
      requiredPermission: PERMISSIONS.MANAGE_SHARING_GROUPS,
      viewerRole: USER_ROLES.TEACHER,
    }
  },
  {
    path: '/teacher/groups/:id',
    name: APP_ROUTE_NAMES.TEACHER_GROUP_DETAIL,
    component: TeacherGroupsView,
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.TEACHER],
      requiredPermission: PERMISSIONS.MANAGE_SHARING_GROUPS,
      viewerRole: USER_ROLES.TEACHER,
    }
  },
  {
    path: '/researcher/groups',
    name: APP_ROUTE_NAMES.RESEARCHER_GROUPS,
    component: TeacherGroupsView,
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.RESEARCHER],
      requiredPermission: PERMISSIONS.MANAGE_SHARING_GROUPS,
      viewerRole: USER_ROLES.RESEARCHER,
    }
  },
  {
    path: '/researcher/groups/:id',
    name: APP_ROUTE_NAMES.RESEARCHER_GROUP_DETAIL,
    component: TeacherGroupsView,
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.RESEARCHER],
      requiredPermission: PERMISSIONS.MANAGE_SHARING_GROUPS,
      viewerRole: USER_ROLES.RESEARCHER,
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const meta = to.meta || {}
  const roleContext = meta.dashboardRole || meta.viewerRole || authStore.activeRole || null
  const routeNeedsResolvedUser =
    meta.redirectAuthenticatedToDashboard ||
    meta.requiresAuth ||
    meta.allowedRoles ||
    meta.requiredPermission

  if (authStore.isLoggedIn && routeNeedsResolvedUser && !authStore.hasFetchedUser) {
    await authStore.fetchUser()
  }

  if (meta.redirectAuthenticatedToDashboard) {
    const landingRoute = getPostAuthLandingRoute(authStore.user, authStore.activeRole)
    if (landingRoute && landingRoute.name !== to.name) {
      next(landingRoute)
      return
    }
  }

  if (meta.requiresAuth && !authStore.isLoggedIn) {
    next({ name: APP_ROUTE_NAMES.HOME })
    return
  }

  if (meta.allowedRoles && !userHasAnyRole(authStore.user, meta.allowedRoles)) {
    next(getDefaultRoleRoute(authStore.user))
    return
  }

  if (meta.requiredPermission && !userHasPermission(authStore.user, meta.requiredPermission, roleContext)) {
    next(getDefaultRoleRoute(authStore.user))
    return
  }

  if (roleContext) {
    authStore.setActiveRole(roleContext)
  }

  next()
})

export default router
